import { Inject } from '@odinjs/odin';

import { BeneficiarioInclusaoEntity, catchRenderException, Endpoint, MotivoInclusao, setPredefinedFiles, StatusBeneficiario } from '@philips-emr/hpms-stipulator-commons';
import { BaseFileUploaderAPI, BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues, ContextualBarAPI, FetchAPI, FieldAPI, FormAPI, MapperAPI, PanelAPI, StampAPI, TextAPI, WizardAPI } from '@philips-emr/tws-components';
import { StepStage } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import { ChangeEvent, Mountable } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { InclusaoBeneficiarioNovaInclusaoAliases as InclusaoAliases, BeneficiarioLoteAliases as LoteAliases } from '../../../codegen';
import { Anexo } from '../../../interfaces/anexo';
import BaseController from '../../BaseController';
import { BeneficiarioLoteData, getBeneficiarioLoteIconColorByStatus, getBeneficiarioLoteIconTypeByStatus, LoteSituacao } from '../lote-utils';

export const identifiers = {
  anexos: 'wizard-step-anexos',
  dadosBeneficiario: 'wizard-step-dados-beneficiario',
  dadosContratacao: 'wizard-step-dados-contratacao',
  portabilidade: 'wizard-step-portabilidade',
  repasse: 'wizard-step-repasse',
  sca: 'wizard-step-sca',
};

export const formAlias = {
  dadosContratacao: 'form-dados-contratacao',
  repasse: 'form-repasse',
  dadosBeneficiario: 'form-dados-beneficiario',
  portabilidade: 'form-portabilidade',
  sca: 'form-sca',
  anexos: 'form-anexos',
};

export interface FormMap { api: FormAPI, alias: string }

interface RecordAnexos {
  anexos: Array<Anexo>;
}

class BaseBeneficiarioLoteController extends BaseController {

  protected mapStepToForm: Record<string, FormMap> = {};
  protected allowEditing: boolean = false;
  protected recordChanges: Record<string, Value> = {};
  protected regraAnexos: Anexo[];

  @Inject(LoteAliases.WIZARD_DETALHES_BENEF_LOTE)
  protected wizardDetalhesBenefLote: WizardAPI;

  @Inject(LoteAliases.STAMP_SITUACAO_LOTE)
  private stampSituacaoApi: StampAPI;

  @Inject(LoteAliases.PANEL_CONTROL_CARD)
  private panelControlCardApi: PanelAPI;

  @Inject(LoteAliases.TEXT_MOTIVO)
  private textMotivoApi: TextAPI;

  @Inject(LoteAliases.CONTEXTUAL_BAR_BENEF_LOTE)
  protected ctxBarBenefLoteApi: ContextualBarAPI;

  @Inject(LoteAliases.FILE_UPLOADER_BENEF_INCLUSAO)
  protected fileUploaderBenefInclusao: BaseFileUploaderAPI<BaseFileUploaderAPIState, BaseFileUploaderAPIStateWithDefaultValues>;

  @Inject(LoteAliases.MAPPER_ANEXOS)
  protected mapperAnexosApi: MapperAPI;

  @Inject(LoteAliases.MAPPER_REGRA_SCA)
  private mapperRegraScaApi: MapperAPI;

  @Inject(LoteAliases.FORM_SCA)
  private formScaApi: FormAPI;

  @Inject(LoteAliases.FETCH_DS_MUNICIPIO)
  protected fetchMunicipio: FetchAPI;

  @Inject(InclusaoAliases.FETCH_CD_MUNICIPIO_IBGE_NASC)
  protected fetchMunicipioNasc: FetchAPI;

  @Inject(LoteAliases.FETCH_TABELA_PRECO)
  protected fetchTabelaPreco: FetchAPI;

  @Inject(LoteAliases.FIELD_DS_MUNICIPIO)
  protected fieldMunicipio: FieldAPI;

  @Inject(LoteAliases.FIELD_TABELA_PRECO)
  protected fieldTabelaPreco: FieldAPI;

  @Inject(LoteAliases.FIELD_ANEXOS)
  private fieldAnexos: FieldAPI;

  protected linkFormAndStep(identifier: string, api: Mountable, alias: string): void {
    this.mapStepToForm = { ...this.mapStepToForm, [identifier]: { api: api as FormAPI, alias } };
  }

  protected setStepDone(identifier: string, api: Mountable): void {
    if (this.wizardDetalhesBenefLote.getCurrentStepIdentifier() === identifier) {
      const formApi = api as FormAPI;
      if (formApi.isValid()) {
        this.wizardDetalhesBenefLote.setStepDone(identifier, true);
      }
    }
  }

  protected getFormMapByIdentifier(identifier: string): FormMap {
    return this.mapStepToForm[identifier];
  }

  protected setReadOnly(readOnly: boolean): void {
    this.wizardDetalhesBenefLote.setReadOnly(readOnly);
    Object.entries(this.mapStepToForm).forEach(([, form]) => form.api.setEditing(!readOnly));
  }

  protected setHiddenSteps(identifier: string, add: boolean = true): void {
    const steps = this.wizardDetalhesBenefLote.getSteps();
    const [currentStepDone] = Object.values(steps).filter(step => {
      const { stage } = step as unknown as Record<string, Value>;

      return stage === StepStage.CURRENT;
    });

    const currentHiddenSteps = this.wizardDetalhesBenefLote.getHiddenSteps();
    const hiddenSteps = currentHiddenSteps.filter(step => step !== identifier) || [];

    if (add) {
      hiddenSteps.push(identifier);
    }

    this.wizardDetalhesBenefLote.setHiddenSteps([...hiddenSteps]);

    if (currentStepDone) {
      this.getFormMapByIdentifier(currentStepDone.identifier).api.validate();
    }
  }

  protected initInclusaoManual(): void {
    this.initAnexos();
    this.initRegrasSCA();
    this.allowEditing = true;
    this.setReadOnly(false);
    this.ctxBarBenefLoteApi.setState({ title: 'stipulator.nova-inclusao' });
  }

  protected initData(): void {
    void this.initFormsData().then(nrSeqPlano => {
      this.initRegrasSCA(nrSeqPlano ? nrSeqPlano : undefined);
    });
    this.initAnexos();
  }

  protected initSCAsBenef(): void {
    const { nrSeqInclusao, nrSeqContrato } = this.historyService.getCurrentRouteParams();

    if (nrSeqInclusao) {
      void HttpService.get(Endpoint.GET.BENEFICIARIO_INCLUSAO_SCA, { nrSeqContrato, nrSeqInclusao })
        .then(respScas => {
          const { body } = respScas;
          if (!body) {
            this.formScaApi.setData({} as Record<string, Value>);
            return;
          }
          this.formScaApi.setData(body as Record<string, Value>);
        });
    }
  }

  protected initRegrasSCA(nrSeqPlanoBenef?: number): void {
    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();
    let params = { nrSeqContrato } as any;

    if (nrSeqPlanoBenef) {
      params = { nrSeqContrato, nrSeqPlanoBenef };
    }

    void HttpService.get(Endpoint.GET.REGRAS_SCA, { ...params })
      .then(resp => {
        const { body } = resp;
        const mapperData = body as Record<string, Value>;
        const hasRegrasSCA = !isEmptyArrayOrObject(mapperData);

        if (hasRegrasSCA) {
          this.mapperRegraScaApi.setData(mapperData);

          this.initSCAsBenef();
        }

        this.setHiddenSteps(identifiers.sca, !hasRegrasSCA);
      });
  }

  protected initAnexos(): void {
    const { nrSeqInclusao, nrSeqContrato, nrSeqLote } = this.historyService.getCurrentRouteParams();

    const hiddeAnexos = (hidde: boolean): void => {
      this.setHiddenSteps(identifiers.anexos, hidde);
    };

    void setPredefinedFiles(15, this.fileUploaderBenefInclusao, ['A', 'BL'])
      .then(regraAnexos => {
        const hasRegrasAnexos = !isEmptyArrayOrObject(regraAnexos);

        this.regraAnexos = regraAnexos as unknown as Anexo[];

        this.fieldAnexos.setVisible(hasRegrasAnexos);

        if (nrSeqInclusao) {
          void HttpService.get<Record<string, Value>>(Endpoint.GET.ANEXOS_BENEFICIARIO_INCLUSAO_LOTE, { nrSeqInclusao, nrSeqContrato, nrSeqLote })
            .then(resp => {
              const { body } = resp;

              if (body && !isEmptyArrayOrObject(body)) {
                this.mapperAnexosApi.setData(body);
                this.getFormMapByIdentifier(identifiers.anexos).api.setEditing(this.allowEditing && hasRegrasAnexos);
                return;
              }

              hiddeAnexos(!hasRegrasAnexos);
            });

          return;
        }

        hiddeAnexos(!hasRegrasAnexos);
      });
  }

  protected async initFormsData(): Promise<number | void> {
    const { nrSeqInclusao, nrSeqLote, nrSeqContrato } = this.historyService.getCurrentRouteParams();

    const response = await HttpService.get<Record<string, Value>>(Endpoint.GET.BENEFICIARIO_INCLUSAO_LOTE, { nrSeqInclusao, nrSeqLote, nrSeqContrato });
    const { body } = response;
    const { nrSeqPlano } = body as unknown as BeneficiarioInclusaoEntity;

    body && this.mapStepToForm && Object.entries(this.mapStepToForm).forEach(([identifier, form]) => {
      if ([identifiers.anexos, identifiers.sca].includes(identifier)) return;

      form.api.setData(body);
    });

    const motivoInclusao = (body as unknown as BeneficiarioInclusaoEntity).motivoInclusao;
    this.afterInitFormsData(motivoInclusao);

    return nrSeqPlano;
  }

  protected async deleteSCAs(): Promise<void> {
    await HttpService.delete(Endpoint.DELETE.SCA, this.historyService.getCurrentRouteParams());
  }

  protected afterInitFormsData(motivoInclusao: MotivoInclusao): void {
    this.validateStatus();
    this.validateMotivoInclusao(motivoInclusao);
  }

  private submitAll(): void {
    const hiddenSteps = this.wizardDetalhesBenefLote.getHiddenSteps() || [];
    Object.entries(this.mapStepToForm).forEach(([identifier, value]) => {
      if (!hiddenSteps.includes(identifier)) {
        value.api.submit();
      }
    });
  }

  protected getBody(): Record<string, Value> {
    this.submitAll();

    const recordAnexos: RecordAnexos = catchRenderException(() => this.getFormMapByIdentifier(identifiers.anexos).api.getRecord());
    const recordDadosBeneficiario = this.getFormMapByIdentifier(identifiers.dadosBeneficiario).api.getRecord();
    const recordDadosContratacao = this.getFormMapByIdentifier(identifiers.dadosContratacao).api.getRecord();
    const recordPortabilidade = this.getFormMapByIdentifier(identifiers.portabilidade).api.getRecord();
    const recordRepasse = this.getFormMapByIdentifier(identifiers.repasse).api.getRecord();
    const recordSca = this.getFormMapByIdentifier(identifiers.sca).api.getRecord();

    if (recordAnexos && recordAnexos.anexos) {

      recordAnexos.anexos = this.handleAnexos(recordAnexos.anexos);

    }

    const entityData = {
      ...recordDadosContratacao,
      ...recordDadosBeneficiario,
      ...recordPortabilidade,
      ...recordRepasse,
      ...this.recordChanges,
    } as unknown as BeneficiarioInclusaoEntity;

    const nrSeqMotivoInclusao = entityData.motivoInclusao?.nrSeqMotivoInclusao;
    const nrSeqCongenere = entityData.congenere?.nrSeqCongenere;
    const cdCbo = entityData.cbo?.cdCbo;

    return {
      ...recordAnexos,
      ...recordSca,
      entity: { ...entityData, nrSeqMotivoInclusao, nrSeqCongenere, cdCbo },
    };
  }

  protected validateMotivoInclusao(movitoInclusao: { iePortabilidade: string }): void {
    this.setHiddenSteps(identifiers.portabilidade, movitoInclusao?.iePortabilidade !== 'S');
  }

  protected validateStatus(): void {
    const { nrSeqLote, nrSeqContrato, nrSeqInclusao } = this.historyService.getCurrentRouteParams();

    void HttpService.get<BeneficiarioLoteData[]>(Endpoint.GET.BENEFICIARIO_LOTE_STATUS, { nrSeqLote, nrSeqContrato, nrSeqInclusao })
      .then(response => {
        const { body } = response;

        if (!body) {
          return;
        }

        const { ieStatus, dsMotivoReprovacao, dsStatus } = body as unknown as BeneficiarioLoteData;

        void HttpService.get<BeneficiarioLoteData[]>(Endpoint.GET.LOTE, { nrSeqLote, nrSeqContrato })
          .then(response => {

            const { body } = response;
            const { ieSituacao } = body as unknown as BeneficiarioLoteData;

            this.allowEditing = ieSituacao === LoteSituacao.PENDENTE || ieSituacao === LoteSituacao.COM_INCONSISTENCIAS;

            this.setReadOnly(!this.allowEditing);

            if (ieStatus === StatusBeneficiario.RO) {
              this.panelControlCardApi.setVisible(true);

              this.textMotivoApi.setValue(dsMotivoReprovacao);

              this.stampSituacaoApi.setColor(getBeneficiarioLoteIconColorByStatus(ieStatus));
              this.stampSituacaoApi.setIcon(getBeneficiarioLoteIconTypeByStatus(ieStatus));
              this.stampSituacaoApi.setLabel(dsStatus);
            }
          });
      });
  }

  private handleAnexos(anexos: Anexo[]): Anexo[] {
    const handledAnexos = anexos.map(anexo => {
      const handledAnexo = { ...anexo };
      const predefinedFileKey = anexo.predefinedFileKey;
      const nrSeqTipoDocumento = this.regraAnexos.find(regraAnexo => +regraAnexo.nrSeqRegraAnexo === +predefinedFileKey)?.nrSeqTipoDocumento ?? 0;
      handledAnexo.nrSeqTipoDocumento = String(nrSeqTipoDocumento);
      return handledAnexo;
    });

    return handledAnexos;
  }

  protected fetchSCAAccordingToProduct(e: ChangeEvent<number>, nrSeqPlanoBenef: number): void {
    this.initRegrasSCA(nrSeqPlanoBenef);
    this.fieldTabelaPreco.setValue(null);
    this.fetchTabelaPreco.setParameters({ ...this.fetchTabelaPreco.getParameters(), nrSeqPlano: e.value ? e.value : null });
    this.fetchTabelaPreco.forceFetch();
  }
}

export default BaseBeneficiarioLoteController;
